import React, { useState, useMemo, useEffect, useCallback } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Sidebar } from "@/components/Sidebar";
import { RightPanel } from "@/components/RightPanel";
import { StationSchematic } from "@/components/StationSchematic";
import { WorkerApp } from "@/components/WorkerApp";
import { IntroOverlay } from "@/components/IntroOverlay";
import { useYardSim } from "@/sim/useYardSim";
import { useT, trackLabel, stationName } from "@/i18n";
import { resolveLang, langUrl } from "@/lib/resolveLang";
import { toast } from "sonner";
import { Download, ArrowLeft, Activity } from "lucide-react";

function App() {
  const [lang, setLang] = useState(() =>
    resolveLang(
      typeof window !== "undefined" ? window.location.pathname : "",
      typeof window !== "undefined" ? window.location.search : "",
      typeof navigator !== "undefined" ? navigator.language : undefined
    )
  );
  const [role, setRole] = useState("dispatcher");
  const [introOpen, setIntroOpen] = useState(true);
  const [mode, setMode] = useState("dwell");
  const [view, setView] = useState("overview");
  const [tab, setTab] = useState("journal");
  const [selectedWagonId, setSelectedWagonId] = useState(null);

  const sim = useYardSim(lang);
  const { yard, events, ocr, onDone, updateWagon, moveWagon, acceptTrain, resolveOcr, reset, exportCSV } = sim;
  const t = useT(lang);
  const closeIntro = useCallback(() => setIntroOpen(false), []);
  // Everything the page wrapper shows is derived from one piece of state, the
  // address included: the URL owns the language, so it is written from `lang`
  // rather than kept in step with it. Running on mount also normalises an
  // address that arrived without a language, or with a legacy ?lang=.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = `${t("appTitle")} · ${t("appSub")}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("docDescription"));
    if (typeof window === "undefined") return;
    const { pathname, search, hash } = window.location;
    const next = langUrl(pathname, search, hash, lang);
    if (`${pathname}${search}${hash}` !== next) window.history.replaceState(null, "", next);
  }, [lang, t]);

  const selectedWagon = useMemo(() => {
    if (!selectedWagonId) return null;
    for (const s of yard.stations)
      for (const tr of s.tracks) {
        const w = tr.wagons.find((x) => x.id === selectedWagonId);
        if (w) return w;
      }
    return null;
  }, [selectedWagonId, yard]);

  const trackOptions = useMemo(() => {
    if (!selectedWagon) return [];
    const st = yard.stations.find((s) => s.code === selectedWagon.st);
    if (!st) return [];
    return st.tracks.map((tr, i) => ({
      id: String(i),
      label: `${st.code} · ${trackLabel(tr.idx, t)} (${tr.count}/${tr.capacity})`,
    }));
  }, [selectedWagon, yard, t]);

  const onWagonClick = (w) => {
    setSelectedWagonId(w.id);
    setTab("wagon");
  };

  const handleReset = () => {
    reset();
    setSelectedWagonId(null);
    setView("overview");
    toast.success(t("dataReset"));
  };

  const focusedStation =
    view !== "overview" ? yard.stations.find((s) => s.id === view) : null;

  const exportCurrent = () => {
    const code = focusedStation ? focusedStation.code : yard.stations[0]?.code;
    if (code) exportCSV(code);
  };

  const overlay = introOpen ? (
    <IntroOverlay t={t} lang={lang} setLang={setLang} onClose={closeIntro} />
  ) : null;

  if (!yard.stations.length) {
    return (
      <div className="h-screen grid place-items-center bg-[#0a0a0c] text-zinc-500">
        <div className="flex flex-col items-center gap-3">
          <Activity className="animate-pulse text-sky-500" size={32} />
          <span className="text-sm font-mono tracking-widest">{t("loadingYard")}</span>
        </div>
      </div>
    );
  }

  if (role === "worker") {
    return (
      <>
        <div inert={introOpen || undefined}>
          <WorkerApp
            yard={yard}
            ocr={ocr}
            lang={lang}
            setLang={setLang}
            setRole={setRole}
            updateWagon={(id, fields) => updateWagon(id, fields, "worker")}
            acceptTrain={(code, tk, trainNo, rows) => acceptTrain(code, tk, trainNo, rows, "worker")}
            resolveOcr={(id, number, action) => resolveOcr(id, number, action, "worker")}
          />
        </div>
        {overlay}
      </>
    );
  }

  const sorting = yard.stations.find((s) => s.type === "sorting");
  const loadings = yard.stations.filter((s) => s.type === "loading");

  return (
    <>
      <div className="yard-bg h-screen w-screen overflow-hidden text-zinc-200 grid grid-cols-12" inert={introOpen || undefined}>
      <div className="col-span-3 xl:col-span-2 h-screen">
        <Sidebar
          yard={yard}
          lang={lang}
          setLang={setLang}
          role={role}
          setRole={setRole}
          mode={mode}
          setMode={setMode}
          view={view}
          setView={setView}
          setSelectedWagon={setSelectedWagonId}
          t={t}
          onReset={handleReset}
        />
      </div>

      <main className="col-span-6 xl:col-span-7 h-screen flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 bg-[#0d0d10]">
          <div className="flex items-center gap-3">
            {focusedStation && (
              <button
                type="button"
                data-testid="back-overview"
                onClick={() => setView("overview")}
                className="text-zinc-500 hover:text-white"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <div>
              <h2
                className="text-lg font-bold tracking-tight text-white leading-none"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {focusedStation
                  ? stationName(focusedStation.code, lang)
                  : t("overview")}
              </h2>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {focusedStation
                  ? `${focusedStation.code} · ${focusedStation.wagon_count} ${t("wagonsOnTrack")}`
                  : `${yard.stations.length} ${t("stations").toLowerCase()} · ${yard.totals.wagons} ${t("wagonsOnTrack")}`}
              </span>
            </div>
          </div>
          <Button
            data-testid="export-btn"
            onClick={exportCurrent}
            className="h-8 gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm text-[11px] border border-zinc-700"
          >
            <Download size={14} /> {t("export")}
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto no-scrollbar p-5 space-y-4">
          {focusedStation ? (
            <StationSchematic
              station={focusedStation}
              mode={mode}
              lang={lang}
              t={t}
              selectedWagonId={selectedWagonId}
              onWagonClick={onWagonClick}
              onDone={onDone}
              large
              focused
            />
          ) : (
            <>
              {sorting && (
                <StationSchematic
                  station={sorting}
                  mode={mode}
                  lang={lang}
                  t={t}
                  selectedWagonId={selectedWagonId}
                  onWagonClick={onWagonClick}
                  onDone={onDone}
                  onFocus={setView}
                  large
                />
              )}
              <div className="flex flex-col gap-4">
                {loadings.map((s) => (
                  <StationSchematic
                    key={s.id}
                    station={s}
                    mode={mode}
                    lang={lang}
                    t={t}
                    selectedWagonId={selectedWagonId}
                    onWagonClick={onWagonClick}
                    onDone={onDone}
                    onFocus={setView}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <div className="col-span-3 h-screen">
        <RightPanel
          selectedWagon={selectedWagon}
          t={t}
          lang={lang}
          events={events}
          ocr={ocr}
          tab={tab}
          setTab={setTab}
          onCloseWagon={() => setSelectedWagonId(null)}
          onSave={(id, fields) => updateWagon(id, fields, "dispatcher")}
          onMove={moveWagon}
          trackOptions={trackOptions}
          onResolveOcr={(id, number, action) => resolveOcr(id, number, action, "dispatcher")}
          yard={yard}
          onAcceptTrain={(code, tk, trainNo, rows) => acceptTrain(code, tk, trainNo, rows, "dispatcher")}
        />
      </div>

      <Toaster position="top-center" theme="dark" />
      </div>
      {overlay}
    </>
  );
}

export default App;
